import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useTheme as useNextTheme } from 'next-themes';
import { themeNames, type ThemeName } from '@/lib/theme-registry';

// Re-export for backward compat
export type { ThemeName };

interface ThemeContextType {
  themeName: ThemeName;
  setThemeName: (theme: ThemeName) => void;
  mode: string | undefined;
  setMode: (mode: string) => void;
  systemTheme: string | undefined;
}

// eslint-disable-next-line react-refresh/only-export-components
export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * CustomThemeProvider manages palette selection (data-theme attribute)
 * Works alongside next-themes ThemeProvider which manages light/dark mode
 *
 * Usage:
 * - themeName: Current palette ('monochrome', 'cyberpunk', 'forest', 'ocean')
 * - setThemeName: Change palette
 * - mode: Light/dark mode ('light', 'dark', 'system')
 * - setMode: Change mode
 * - systemTheme: Resolved system theme ('light' or 'dark')
 */
export function CustomThemeProvider({ children }: { children: ReactNode }) {
  const { theme: mode, setTheme: setMode, systemTheme } = useNextTheme();
  const [themeName, setThemeNameState] = useState<ThemeName>('monochrome');
  const [mounted, setMounted] = useState(false);

  // Load saved theme after mount to avoid hydration mismatch
  useEffect(() => {
    setMounted(true);
    const saved = localStorage.getItem('theme-name') as ThemeName;
    if (saved && (themeNames as readonly string[]).includes(saved)) {
      setThemeNameState(saved as ThemeName);
    }
  }, []);

  // Apply data-theme attribute when themeName changes
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeName);
    localStorage.setItem('theme-name', themeName);
  }, [themeName]);

  const setThemeName = (newTheme: ThemeName) => {
    setThemeNameState(newTheme);
  };

  return (
    <ThemeContext.Provider
      value={{
        themeName,
        setThemeName,
        mode,
        setMode,
        systemTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
